import { MENU_NAVIGATION } from '@bundles/constant/routes'
import { withStagingProxyVersionQuery } from '@fordeer/staging-proxy-version'
import { type MouseEvent, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * App Bridge v4 `<s-app-nav>` — SPA navigation without ClientRouter.
 *
 * Staging/proxy: `s-link` href + navigate go through
 * `@fordeer/staging-proxy-version` so Cloudflare Proxy V3 `clientVer`/`serverVer`
 * stay sticky (URL query, else head meta). `MENU_NAVIGATION` hrefs stay clean.
 */
const AppNavigation = () => {
  const navigate = useNavigate()
  const { search } = useLocation()

  /** Build nav target; staging preserves proxy version query. */
  const toNavHref = useCallback(
    (path: string) => withStagingProxyVersionQuery(path, { search }),
    [search],
  )

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const link = (event.target as Element | null)?.closest('s-link')
      if (!link) return

      const href = link.getAttribute('href')
      if (!href) return

      event.preventDefault()
      // Href already includes staging version query when applicable.
      navigate(href)
    },
    [navigate],
  )

  return (
    <s-app-nav onClick={handleClick}>
      {MENU_NAVIGATION.map(({ href, label, rel }) => {
        const navHref = toNavHref(href)
        return (
          <s-link key={href} href={navHref} {...(rel ? { rel } : {})}>
            {label}
          </s-link>
        )
      })}
    </s-app-nav>
  )
}

export default AppNavigation
